<template>
    <div class="drop-zone" @click="triggerFileInput">
      <input
        ref="fileInput"
        type="file"
        :accept="accepted"
        style="display: none"
        @change="onFileChange"
      />
      <div class="preview" v-if="previewUrl">
        <img :src="previewUrl" alt="Preview" />
        <button class="remove-button" @click.stop="removeImage" title="Remove File"><v-icon small>fa fa-trash</v-icon></button>
      </div>
      <div class="placeholder" v-else>
        {{ text }}
      </div>
    </div>
  </template>
  
  <script>
  import companyAPI from "@/api/company.js";
  
  export default {
    name: "CompanyLogoUpload",
    props: {
      image: {
        type: [String, File],
        default: null,
      },
      text: {
        type: String,
        default: "Click here to upload image",
      },
      type: {
        type: String,
        default: "company-logo",
      },
      accepted: {
        type: String,
        default: "image/jpeg,image/png",
      },
    },
    data() {
      return {
        previewUrl: null,
      };
    },
    watch: {
      image: {
        immediate: true,
        handler(val) {
          if (typeof val === "string") {
            this.previewUrl = val;
          } else if (val instanceof File) {
            this.previewUrl = URL.createObjectURL(val);
          } else {
            this.previewUrl = null;
          }
        },
      },
    },
    methods: {
      triggerFileInput() {
        this.$refs.fileInput.click();
      },
      onFileChange(event) {
        const file = event.target.files[0];
        if (!file) return;
  
        const isValidType = this.accepted.split(",").includes(file.type);
        if (!isValidType) {
          this.$snotify.error("Invalid file format.");
          return;
        }
  
        this.resizeImage(file, 237, 115)
          .then((resizedBlob) => {
            
            const resizedFile = new File([resizedBlob], file.name, {
              type: resizedBlob.type,
              lastModified: Date.now(),
            });
            
            let fileDetails = {
              size: resizedFile.size,
              name: resizedFile.name,
              type: resizedFile.type
            };

            

            // Save waits while this runs (s3Uploading), and the file's address
            // is kept only once S3 has it: before 9 Oct 2026 this upload was
            // fire and forget, so a failed one went unreported and Save sent
            // the address of a file that did not exist.
            this.$store.dispatch("setS3Uploading", true);
            companyAPI.fileSign(fileDetails)
              .then((response) => {
                let signature = response.data.signature;
                let endPoint = 'https:' + response.data.postEndpoint;
                let url = endPoint + '/' + signature.key;
                const fileName = url.split('/').pop(); // "2d20bff1-0899-4254-b8b7-d4ed042794cb.png"

                fileDetails.name = fileName;
                signature.file = resizedFile;

                const formData = new FormData();
                for (const key in signature) {
                  formData.append(key, signature[key]);
                }
                // Upload file directly to S3 using the signed URL
                return fetch(endPoint, {
                  method: 'POST',
                  body: formData // No headers needed, especially no Authorization
                }).then((res) => {
                  if (!res.ok) throw new Error("S3 answered " + res.status);
                  this.$store.dispatch("setS3Url", url);
                  this.$store.dispatch("setFileDetails", fileDetails);
                });
              })
              .catch(() => {
                this.removeImage();
                this.$snotify.error("The logo could not be uploaded. Please try again, or use a different PNG or JPG file.");
              })
              .finally(() => {
                this.$store.dispatch("setS3Uploading", false);
              });

           
            // Show preview
            this.previewUrl = URL.createObjectURL(resizedFile);
          })
          .catch((err) => {
            console.error("Image resize failed:", err);
            this.$snotify.error("Image processing failed.");
          });
  
        event.target.value = "";
      },
      resizeImage(file, maxWidth, maxHeight) {
        return new Promise((resolve, reject) => {
          const img = new Image();
          const reader = new FileReader();
  
          reader.onload = (e) => {
            img.src = e.target.result;
          };
          reader.onerror = reject;
  
          img.onload = () => {
            const originalWidth = img.width;
            const originalHeight = img.height;

            // Warn if too small or too large
            if (originalWidth < 300 || originalHeight < 70) {
              this.$snotify.warning("The uploaded image is small. It may appear pixelated when stretched to 475×115.");
            } else if (originalWidth > 600 || originalHeight > 200) {
              this.$snotify.warning("The uploaded image is quite large and will be scaled down to 475×115.");
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
  
            const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
            const newWidth = Math.round(img.width * ratio);
            const newHeight = Math.round(img.height * ratio);
  
            canvas.width = newWidth;
            canvas.height = newHeight;
  
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, newWidth, newHeight);
  
            const type = file.type.includes("jpeg") ? "image/jpeg" : "image/png";
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error("Canvas toBlob failed."));
                return;
              }
              resolve(blob);
            }, type, 0.9); // 90% quality
          };
  
          reader.readAsDataURL(file);
        });
      },
      removeImage() {
      this.previewUrl = null;
      this.selectedFile = null;
      this.$refs.fileInput.value = null;
      this.$emit("file-removed"); // Optional
      this.$store.dispatch("setFileDetails", {})
      this.$store.dispatch("setS3Url", '')
    }
    },
  };
  </script>
  
  <style scoped>
  .drop-zone {
    border: 2px dashed #ccc;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.6);
  }
  
  .preview img {
    max-height: 80px;
    object-fit: contain;
    max-width: 100%;
  }
  
  .placeholder {
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }

  .remove-button {
  /* position: absolute; */
  top: 5px;
  right: 5px;
  border: none;
  padding: 5px 8px;
  cursor: pointer;
  margin: 0 auto;
  display: block;
}
.remove-button i{
  color: red;
}
  </style>
  